import { MANUFACTURING_MATERIAL_OPTIONS } from "./machiningMaterials";

const INCH_TO_MM = 25.4;
const PI = Math.PI;

const n = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const positive = (value, label, errors) => {
  const parsed = n(value);
  if (value === "" || value === null || value === undefined) {
    errors.push(`${label}: more data required.`);
  } else if (parsed <= 0) {
    errors.push(`${label} must be greater than zero.`);
  }
  return parsed;
};

const nonNegative = (value, label, errors) => {
  const parsed = n(value);
  if (value === "" || value === null || value === undefined) {
    errors.push(`${label}: more data required.`);
  } else if (parsed < 0) {
    errors.push(`${label} cannot be negative.`);
  }
  return parsed;
};

const round = (value, digits = 4) => Number(n(value).toFixed(digits));
const money = (value) => `$${n(value).toFixed(2)}`;
const pct = (value) => `${(n(value) * 100).toFixed(2)}%`;
const dim = (value, unit = "inch") => `${n(value).toFixed(unit === "metric" ? 3 : 4)} ${unit === "metric" ? "mm" : "in"}`;
const speed = (value, unit = "inch") => `${n(value).toFixed(1)} ${unit === "metric" ? "m/min" : "SFM"}`;

const field = (key, label, type = "number", extra = {}) => ({ key, label, type, ...extra });
const unitField = field("unit", "Units", "select", { options: [["inch", "Inch"], ["metric", "Metric"]] });

const wrap = ({ values = {}, warnings = [], formulas = [], assumptions = [] }) => ({
  values,
  warnings,
  formulas,
  assumptions,
});

const calculator = (config) => ({
  ...config,
  tags: config.tags || [],
  assumptions: config.assumptions || [],
});

const materialDensity = Object.fromEntries(
  MANUFACTURING_MATERIAL_OPTIONS.map((material) => [
    material.id,
    {
      label: material.label,
      lbIn3: material.densityLbIn3,
      gCm3: material.densityLbIn3 * 27.6799,
    },
  ])
);

const materialOptions = Object.entries(materialDensity).map(([id, item]) => [id, item.label]);

const calculateRectWeight = (p) => {
  const errors = [];
  const unit = p.unit || "inch";
  const length = positive(p.length, "Length", errors);
  const width = positive(p.width, "Width", errors);
  const thickness = positive(p.thickness, "Thickness", errors);
  const quantity = positive(p.quantity, "Quantity", errors);
  const material = materialDensity[p.material] || materialDensity.mild_steel;
  if (errors.length) return { errors };
  const volumeIn3 = unit === "metric"
    ? (length * width * thickness) / 16387.064
    : length * width * thickness;
  const weightLb = volumeIn3 * material.lbIn3;
  return wrap({
    values: {
      "Material": material.label,
      "Volume": unit === "metric" ? `${(volumeIn3 * 16387.064).toFixed(1)} mm^3` : `${volumeIn3.toFixed(3)} in^3`,
      "Weight per Part": `${weightLb.toFixed(3)} lb / ${(weightLb * 0.453592).toFixed(3)} kg`,
      "Total Weight": `${(weightLb * quantity).toFixed(3)} lb / ${(weightLb * quantity * 0.453592).toFixed(3)} kg`,
    },
    formulas: ["Volume = length x width x thickness", "Weight = volume x density x quantity"],
    assumptions: ["Density values are common shop estimating values; use supplier weight for final quotes."],
  });
};

const calculateCylWeight = (p) => {
  const errors = [];
  const unit = p.unit || "inch";
  const diameter = positive(p.diameter, "Diameter", errors);
  const length = positive(p.length, "Length", errors);
  const quantity = positive(p.quantity, "Quantity", errors);
  const material = materialDensity[p.material] || materialDensity.mild_steel;
  if (errors.length) return { errors };
  const volumeIn3 = unit === "metric"
    ? (PI * (diameter ** 2) / 4 * length) / 16387.064
    : PI * (diameter ** 2) / 4 * length;
  const weightLb = volumeIn3 * material.lbIn3;
  return wrap({
    values: {
      "Material": material.label,
      "Volume": unit === "metric" ? `${(volumeIn3 * 16387.064).toFixed(1)} mm^3` : `${volumeIn3.toFixed(3)} in^3`,
      "Weight per Part": `${weightLb.toFixed(3)} lb / ${(weightLb * 0.453592).toFixed(3)} kg`,
      "Total Weight": `${(weightLb * quantity).toFixed(3)} lb / ${(weightLb * quantity * 0.453592).toFixed(3)} kg`,
    },
    formulas: ["Round volume = pi x diameter^2 / 4 x length", "Weight = volume x density x quantity"],
  });
};

export const PROFESSIONAL_CALCULATORS = {
  "surface-finish": calculator({
    title: "Surface Finish Estimate",
    category: "machining",
    description: "Estimate theoretical turning finish from feed per revolution and tool nose radius.",
    initial: { unit: "inch", feedPerRev: 0.006, noseRadius: 0.03125 },
    fields: [unitField, field("feedPerRev", "Feed per Revolution"), field("noseRadius", "Tool Nose Radius")],
    calculate: (p) => {
      const errors = [];
      const f = positive(p.feedPerRev, "Feed per revolution", errors);
      const r = positive(p.noseRadius, "Tool nose radius", errors);
      if (errors.length) return { errors };
      const ra = (f ** 2) / (32 * r);
      const raMicroIn = p.unit === "metric" ? (ra / 25.4) * 1000000 : ra * 1000000;
      const raMicroM = raMicroIn * 0.0254;
      return wrap({
        values: { "Estimated Ra": `${raMicroIn.toFixed(1)} microin / ${raMicroM.toFixed(3)} micrometer`, "Cusp Height": dim(ra, p.unit) },
        formulas: ["Theoretical turning roughness Ra ~= feed^2 / (32 x nose radius)"],
        assumptions: ["Theoretical only; insert geometry, wiper flats, tool pressure, vibration, and material behavior change actual finish."],
      });
    },
    tags: ["turning", "lathe", "finish", "ra", "nose radius"],
  }),
  "stepover-scallop": calculator({
    title: "Step-over / Scallop Height",
    category: "machining",
    description: "Estimate ball end mill scallop height from cutter radius and stepover.",
    initial: { unit: "inch", cutterDiameter: 0.5, stepover: 0.03 },
    fields: [unitField, field("cutterDiameter", "Ball End Mill Diameter"), field("stepover", "Step-over")],
    calculate: (p) => {
      const errors = [];
      const diameter = positive(p.cutterDiameter, "Cutter diameter", errors);
      const stepover = positive(p.stepover, "Step-over", errors);
      if (stepover >= diameter) errors.push("Step-over should be smaller than cutter diameter for this formula.");
      if (errors.length) return { errors };
      const radius = diameter / 2;
      const scallop = radius - Math.sqrt(radius ** 2 - (stepover / 2) ** 2);
      return wrap({
        values: { "Scallop Height": dim(scallop, p.unit), "Cutter Radius": dim(radius, p.unit) },
        formulas: ["Scallop height = R - sqrt(R^2 - (stepover / 2)^2)"],
        assumptions: ["Applies to ball-end surfacing geometry. Actual finish depends on tool runout, tilt, material, and toolpath."],
      });
    },
    tags: ["scallop", "3d milling", "surface finish", "ball mill"],
  }),
  "feed-chipload": calculator({
    title: "Feed Rate from Chip Load",
    category: "machining",
    description: "Calculate mill feedrate from RPM, flute count, and chip load per tooth.",
    initial: { unit: "inch", rpm: 3000, flutes: 4, chipLoad: 0.0025 },
    fields: [unitField, field("rpm", "Spindle RPM"), field("flutes", "Flutes"), field("chipLoad", "Chip Load per Tooth")],
    calculate: (p) => {
      const errors = [];
      const rpm = positive(p.rpm, "RPM", errors);
      const flutes = positive(p.flutes, "Flutes", errors);
      const chip = positive(p.chipLoad, "Chip load", errors);
      if (errors.length) return { errors };
      const feed = rpm * flutes * chip;
      return wrap({
        values: { "Feed Rate": `${feed.toFixed(p.unit === "metric" ? 2 : 3)} ${p.unit === "metric" ? "mm/min" : "IPM"}`, "Feed per Rev": dim(flutes * chip, p.unit) },
        formulas: ["Feedrate = RPM x flutes x chip load per tooth"],
      });
    },
    tags: ["chip load", "feedrate", "ipm", "mm/min"],
  }),
  "mrr": calculator({
    title: "Material Removal Rate",
    category: "machining",
    description: "Calculate rectangular milling MRR from width, depth, and feedrate.",
    initial: { unit: "inch", width: 0.5, depth: 0.1, feedrate: 20 },
    fields: [unitField, field("width", "Width of Cut"), field("depth", "Depth of Cut"), field("feedrate", "Feedrate")],
    calculate: (p) => {
      const errors = [];
      const width = positive(p.width, "Width of cut", errors);
      const depth = positive(p.depth, "Depth of cut", errors);
      const feedrate = positive(p.feedrate, "Feedrate", errors);
      if (errors.length) return { errors };
      const mrr = width * depth * feedrate;
      return wrap({
        values: { "MRR": `${mrr.toFixed(3)} ${p.unit === "metric" ? "mm^3/min" : "in^3/min"}` },
        formulas: ["MRR = width of cut x depth of cut x feedrate"],
        assumptions: ["Rectangular engagement estimate; adaptive paths and turning require process-specific geometry."],
      });
    },
    tags: ["mrr", "material removal", "roughing"],
  }),
  "arc-center": calculator({
    title: "Arc Center I/J Calculator",
    category: "cnc-gcode",
    description: "Calculate I and J center offsets from arc start and center coordinates.",
    initial: { unit: "inch", startX: 0, startY: 0, centerX: 1, centerY: 0 },
    fields: [unitField, field("startX", "Start X"), field("startY", "Start Y"), field("centerX", "Center X"), field("centerY", "Center Y")],
    calculate: (p) => {
      const i = n(p.centerX) - n(p.startX);
      const j = n(p.centerY) - n(p.startY);
      const radius = Math.sqrt(i ** 2 + j ** 2);
      return wrap({
        values: { "I Offset": dim(i, p.unit), "J Offset": dim(j, p.unit), "Arc Radius": dim(radius, p.unit) },
        formulas: ["I = center X - start X", "J = center Y - start Y", "Radius = sqrt(I^2 + J^2)"],
        assumptions: ["Most Fanuc/Haas-style controls use incremental I/J from arc start; verify absolute/incremental arc-center mode."],
      });
    },
    tags: ["I J", "arc", "g02", "g03"],
  }),
  "work-offset-shift": calculator({
    title: "Work Offset Shift",
    category: "cnc-gcode",
    description: "Calculate corrected work offset values from measured part error.",
    initial: { unit: "inch", currentX: 0, currentY: 0, currentZ: 0, errorX: 0.003, errorY: -0.002, errorZ: 0.001 },
    fields: [unitField, field("currentX", "Current Work Offset X"), field("currentY", "Current Work Offset Y"), field("currentZ", "Current Work Offset Z"), field("errorX", "Measured Error X"), field("errorY", "Measured Error Y"), field("errorZ", "Measured Error Z")],
    calculate: (p) => wrap({
      values: {
        "New X Offset": dim(n(p.currentX) - n(p.errorX), p.unit),
        "New Y Offset": dim(n(p.currentY) - n(p.errorY), p.unit),
        "New Z Offset": dim(n(p.currentZ) - n(p.errorZ), p.unit),
      },
      formulas: ["Corrected offset = current offset - measured part error"],
      assumptions: ["Sign convention depends on how the error was measured. Verify with a small correction first."],
    }),
    tags: ["g54", "work offset", "shift", "correction"],
  }),
  "safe-start-block": calculator({
    title: "Safe Start Block Generator",
    category: "cnc-gcode",
    description: "Build a conservative starter modal block for common mill or lathe controls.",
    initial: { machine: "mill", unitMode: "G20", plane: "G17", workOffset: "G54", coolant: "M08", spindle: "M03", rpm: 2500 },
    fields: [
      field("machine", "Machine Type", "select", { options: [["mill", "Mill"], ["lathe", "Lathe"]] }),
      field("unitMode", "Unit Mode", "select", { options: [["G20", "G20 Inch"], ["G21", "G21 Metric"]] }),
      field("plane", "Plane", "select", { options: [["G17", "G17 XY"], ["G18", "G18 XZ"], ["G19", "G19 YZ"]] }),
      field("workOffset", "Work Offset", "text"),
      field("spindle", "Spindle Direction", "select", { options: [["M03", "M03 Clockwise"], ["M04", "M04 Counterclockwise"]] }),
      field("rpm", "RPM"),
      field("coolant", "Coolant", "select", { options: [["M08", "M08 Flood"], ["M07", "M07 Mist"], ["", "Off"]] }),
    ],
    calculate: (p) => {
      const block = `${p.unitMode} ${p.machine === "lathe" ? "G18" : p.plane} G40 G49 G80 G90 ${p.workOffset || "G54"} ${p.spindle} S${round(p.rpm, 0)} ${p.coolant || ""}`.trim();
      return wrap({
        values: { "Starter Block": block },
        formulas: ["Safe start block clears common modal states: cutter comp, length comp, canned cycles, absolute mode, units, plane, work offset."],
        assumptions: ["Starter block must match the exact control, machine options, post style, and shop standard."],
      });
    },
    tags: ["safe start", "g-code", "modal", "G54"],
  }),
  "thread-percentage": calculator({
    title: "Thread Percentage Calculator",
    category: "threads",
    description: "Estimate percent of thread for 60-degree internal threads from major diameter, pitch, and tap drill.",
    initial: { unit: "inch", majorDiameter: 0.25, tpi: 20, tapDrill: 0.201 },
    fields: [unitField, field("majorDiameter", "Major Diameter"), field("tpi", "Threads per Inch / 1 over pitch"), field("tapDrill", "Tap Drill Diameter")],
    calculate: (p) => {
      const errors = [];
      const major = positive(p.majorDiameter, "Major diameter", errors);
      const tpi = positive(p.tpi, "TPI", errors);
      const drill = positive(p.tapDrill, "Tap drill", errors);
      if (drill >= major) errors.push("Tap drill must be smaller than major diameter.");
      if (errors.length) return { errors };
      const pitch = p.unit === "metric" ? 1 / tpi : 1 / tpi;
      const theoreticalDepth = 0.649519 * pitch;
      const actualDepth = (major - drill) / 2;
      const percent = actualDepth / theoreticalDepth;
      return wrap({
        values: { "Thread Percentage": pct(percent), "Pitch": dim(pitch, p.unit), "Radial Thread Depth": dim(actualDepth, p.unit) },
        formulas: ["60-degree theoretical internal thread depth = 0.649519 x pitch", "% thread = actual radial depth / theoretical radial depth"],
        assumptions: ["This is simplified 60-degree thread math. Use standard tables for final class-of-fit, plating, and forming taps."],
      });
    },
    tags: ["tap drill", "thread percentage", "60 degree"],
  }),
  "thread-engagement": calculator({
    title: "Thread Engagement",
    category: "threads",
    description: "Calculate length of engagement in diameters and basic thread turns.",
    initial: { unit: "inch", engagementLength: 0.375, nominalDiameter: 0.25, pitch: 0.05 },
    fields: [unitField, field("engagementLength", "Engagement Length"), field("nominalDiameter", "Nominal Diameter"), field("pitch", "Pitch / Lead")],
    calculate: (p) => {
      const errors = [];
      const length = positive(p.engagementLength, "Engagement length", errors);
      const diameter = positive(p.nominalDiameter, "Nominal diameter", errors);
      const pitchValue = positive(p.pitch, "Pitch", errors);
      if (errors.length) return { errors };
      return wrap({
        values: { "Engagement in Diameters": `${(length / diameter).toFixed(2)}D`, "Engaged Threads": `${(length / pitchValue).toFixed(1)} turns` },
        formulas: ["Engagement in diameters = length / nominal diameter", "Engaged turns = length / pitch"],
        assumptions: ["Thread strength depends on material, class, thread form, percent thread, and fastener strength."],
      });
    },
    tags: ["engagement", "threads", "turns"],
  }),
  "thread-cut-depth": calculator({
    title: "Thread Cutting Depth",
    category: "threads",
    description: "Calculate theoretical and practical Unified or metric 60-degree thread geometry from a thread designation or manual size.",
    initial: { unit: "inch", designation: "1/4-20", threadType: "external", threadClass: "2A", majorDiameter: "", tpi: "", pitch: "", threadPercentage: 75, compoundAngle: 29.5, pitchDiameterMin: "", pitchDiameterMax: "", measuredOverWires: "" },
    fields: [
      unitField,
      field("designation", "Thread Designation", "select", { options: [
        ["1/4-20", "1/4-20 UNC"],
        ["1/4-28", "1/4-28 UNF"],
        ["3/8-16", "3/8-16 UNC"],
        ["3/8-24", "3/8-24 UNF"],
        ["1/2-13", "1/2-13 UNC"],
        ["1/2-20", "1/2-20 UNF"],
        ["M6x1", "M6 x 1"],
        ["M8x1.25", "M8 x 1.25"],
        ["M10x1.5", "M10 x 1.5"],
        ["M12x1.75", "M12 x 1.75"],
        ["manual", "Manual major diameter and pitch/TPI"],
      ] }),
      field("threadType", "Thread Type", "select", { options: [["external", "External"], ["internal", "Internal"]] }),
      field("threadClass", "Thread Class", "select", { options: [["1A", "1A"], ["2A", "2A"], ["3A", "3A"], ["1B", "1B"], ["2B", "2B"], ["3B", "3B"], ["6g", "6g"], ["6H", "6H"]] }),
      field("majorDiameter", "Manual Major Diameter"),
      field("tpi", "Manual TPI"),
      field("pitch", "Manual Metric Pitch"),
      field("threadPercentage", "Practical Thread Percentage"),
      field("compoundAngle", "Compound Infeed Angle"),
      field("pitchDiameterMin", "Pitch Diameter Minimum from Standard/Drawing"),
      field("pitchDiameterMax", "Pitch Diameter Maximum from Standard/Drawing"),
      field("measuredOverWires", "Actual Measurement Over Wires"),
    ],
    calculate: (p) => {
      const errors = [];
      const designation = String(p.designation || "manual");
      const inchMatch = designation.match(/^(\d+)\/(\d+)-(\d+)$/);
      const metricMatch = designation.match(/^M([\d.]+)x([\d.]+)$/i);
      let unit = p.unit || "inch";
      let major = n(p.majorDiameter);
      let pitchValue = 0;
      let tpi = n(p.tpi);
      if (inchMatch) {
        unit = "inch";
        major = Number(inchMatch[1]) / Number(inchMatch[2]);
        tpi = Number(inchMatch[3]);
        pitchValue = 1 / tpi;
      } else if (metricMatch) {
        unit = "metric";
        major = Number(metricMatch[1]);
        pitchValue = Number(metricMatch[2]);
        tpi = INCH_TO_MM / pitchValue;
      } else {
        major = positive(p.majorDiameter, "Major diameter", errors);
        if (unit === "metric") {
          pitchValue = positive(p.pitch, "Metric pitch", errors);
          tpi = INCH_TO_MM / pitchValue;
        } else {
          tpi = positive(p.tpi, "TPI", errors);
          pitchValue = tpi > 0 ? 1 / tpi : 0;
        }
      }
      const percentage = nonNegative(p.threadPercentage, "Thread percentage", errors);
      if (percentage <= 0 || percentage > 100) errors.push("Thread percentage must be greater than 0 and no more than 100.");
      if (p.threadType === "external" && ["1B", "2B", "3B", "6H"].includes(p.threadClass)) {
        errors.push("External threads require an external class such as 1A, 2A, 3A, or 6g.");
      }
      if (p.threadType === "internal" && ["1A", "2A", "3A", "6g"].includes(p.threadClass)) {
        errors.push("Internal threads require an internal class such as 1B, 2B, 3B, or 6H.");
      }
      const hasMinimum = p.pitchDiameterMin !== "" && p.pitchDiameterMin !== undefined;
      const hasMaximum = p.pitchDiameterMax !== "" && p.pitchDiameterMax !== undefined;
      const pitchDiameterMinimum = n(p.pitchDiameterMin);
      const pitchDiameterMaximum = n(p.pitchDiameterMax);
      if (hasMinimum !== hasMaximum) {
        errors.push("Enter both pitch-diameter limits from the controlling standard/drawing, or leave both empty.");
      } else if (
        hasMinimum &&
        (pitchDiameterMinimum <= 0 || pitchDiameterMaximum < pitchDiameterMinimum)
      ) {
        errors.push("Pitch-diameter maximum must be at least the positive minimum.");
      }
      if (errors.length) return { errors };
      const sharpVDepth = 0.8660254 * pitchValue;
      const basicRadialDepth = p.threadType === "internal"
        ? 0.541266 * pitchValue
        : 0.613434 * pitchValue;
      const practicalRadialDepth = basicRadialDepth * (percentage / 100);
      const diameterReduction = practicalRadialDepth * 2;
      const basicPitchDiameter = major - 0.649519 * pitchValue;
      const practicalMinorDiameter = major - diameterReduction;
      const basicMinorDiameter = major - (
        p.threadType === "internal" ? 1.082532 : 1.226869
      ) * pitchValue;
      const compoundAngle = n(p.compoundAngle);
      const compoundTravel = practicalRadialDepth / Math.cos((compoundAngle * PI) / 180);
      const hasClassLimits = hasMinimum && hasMaximum;
      const inspectionPitchDiameter = hasClassLimits
        ? (pitchDiameterMinimum + pitchDiameterMaximum) / 2
        : basicPitchDiameter;
      const bestWire = 0.5773502692 * pitchValue;
      const wireConstant = 0.8660254038 * pitchValue;
      const measurementOverWires =
        inspectionPitchDiameter + (3 * bestWire) - wireConstant;
      const measurementOverWiresMinimum = hasClassLimits
        ? pitchDiameterMinimum + (3 * bestWire) - wireConstant
        : null;
      const measurementOverWiresMaximum = hasClassLimits
        ? pitchDiameterMaximum + (3 * bestWire) - wireConstant
        : null;
      const measuredOverWires = n(p.measuredOverWires);
      const hasMeasuredOverWires =
        p.measuredOverWires !== "" && p.measuredOverWires !== undefined;
      const wireAcceptance = p.threadType !== "external"
        ? "Use an internal thread gage or qualified internal measurement method"
        : !hasClassLimits
          ? "Enter authoritative pitch-diameter limits for pass/fail"
          : !hasMeasuredOverWires
            ? "Enter actual measurement over wires for pass/fail"
            : measuredOverWires >= measurementOverWiresMinimum &&
                measuredOverWires <= measurementOverWiresMaximum
              ? "PASS"
              : "FAIL";
      return wrap({
        values: {
          "Thread Designation": designation === "manual" ? "Manual" : designation,
          "Thread Form": `${unit === "metric" ? "ISO Metric" : "Unified"} 60-degree`,
          "Thread Type / Class": `${p.threadType === "internal" ? "Internal" : "External"} ${p.threadClass}`,
          "Major Diameter": dim(major, unit),
          "Pitch": dim(pitchValue, unit),
          "Threads per Inch": tpi.toFixed(3),
          "Basic Pitch Diameter": dim(basicPitchDiameter, unit),
          "Basic Minor Diameter": dim(basicMinorDiameter, unit),
          "Sharp V Depth": dim(sharpVDepth, unit),
          "Basic Radial Thread Depth": dim(basicRadialDepth, unit),
          [`Practical Radial Depth at ${percentage.toFixed(1)}%`]: dim(practicalRadialDepth, unit),
          "Diameter Reduction": dim(diameterReduction, unit),
          "Practical Minor Diameter": dim(practicalMinorDiameter, unit),
          "Compound Dial Travel": dim(compoundTravel, unit),
          "Class Pitch Diameter Limits": hasClassLimits
            ? `${dim(pitchDiameterMinimum, unit)} to ${dim(pitchDiameterMaximum, unit)}`
            : "Reference the controlling ASME B1.1 / ISO 965 table or drawing",
          "Best Wire Size (60-degree)": p.threadType === "external"
            ? dim(bestWire, unit)
            : "External-thread inspection only",
          "Measurement Over Wires at Target PD": p.threadType === "external"
            ? dim(measurementOverWires, unit)
            : "Not applicable to ordinary internal-thread inspection",
          "Measurement Over Wires Limits": p.threadType === "external" && hasClassLimits
            ? `${dim(measurementOverWiresMinimum, unit)} to ${dim(measurementOverWiresMaximum, unit)}`
            : "Enter authoritative pitch-diameter limits",
          "Measured Over Wires Status": wireAcceptance,
        },
        formulas: [
          "60-degree sharp-V height H = 0.8660254 x pitch",
          "Unified/ISO basic pitch diameter = major diameter - 0.649519 x pitch",
          p.threadType === "internal"
            ? "Basic internal minor diameter = major diameter - 1.082532 x pitch"
            : "Basic external minor diameter = major diameter - 1.226869 x pitch",
          "Practical radial depth = basic radial depth x selected thread percentage",
          "Diameter reduction = 2 x practical radial depth",
          "Compound dial travel = radial depth / cos(compound angle)",
          "Best wire for a 60-degree thread = 0.5773503 x pitch",
          "Measurement over wires M = pitch diameter + 3 x wire diameter - 0.8660254 x pitch",
        ],
        assumptions: [
          "Basic geometry is theoretical. Final class limits must come from the controlling ASME B1.1, ISO 965, drawing, or certified thread table; ManuCalc does not generate class tolerances from generic factors.",
          "Single-point threads should be finished by pitch diameter measurement, thread wires, ring/plug gage, or the drawing requirement rather than dial depth alone.",
          "A 29 to 29.5 degree compound setting is common for manual Unified threading; CNC threading normally uses the control cycle and insert manufacturer's infeed strategy.",
        ],
      });
    },
    tags: ["single point", "threading", "compound", "depth", "Unified", "metric", "thread class"],
  }),
  "reamer-allowance": calculator({
    title: "Reamer Allowance",
    category: "holemaking",
    description: "Recommend a practical drill undersize allowance before reaming.",
    initial: { unit: "inch", finishHole: 0.5 },
    fields: [unitField, field("finishHole", "Finished Reamed Hole Size")],
    calculate: (p) => {
      const errors = [];
      const hole = positive(p.finishHole, "Finished hole", errors);
      if (errors.length) return { errors };
      const allowance = hole <= 0.25 ? 0.005 : hole <= 0.5 ? 0.008 : hole <= 1 ? 0.012 : 0.018;
      const adj = p.unit === "metric" ? allowance * INCH_TO_MM : allowance;
      return wrap({
        values: { "Recommended Allowance": dim(adj, p.unit), "Prep Hole Target": dim(hole - adj, p.unit) },
        formulas: ["Prep hole target = finished hole - reaming allowance"],
        assumptions: ["Allowance is a common shop starting range. Reamer type, material, coolant, and hole depth matter."],
      });
    },
    tags: ["ream", "reamer", "hole", "allowance"],
  }),
  "spot-drill-diameter": calculator({
    title: "Spot Drill Diameter",
    category: "holemaking",
    description: "Calculate spot/chamfer top diameter from tool angle and spot depth.",
    initial: { unit: "inch", toolAngle: 90, depth: 0.03 },
    fields: [unitField, field("toolAngle", "Spot Drill Included Angle"), field("depth", "Spot Depth")],
    calculate: (p) => {
      const errors = [];
      const angle = positive(p.toolAngle, "Tool angle", errors);
      const depth = positive(p.depth, "Depth", errors);
      if (angle >= 180) errors.push("Tool angle must be less than 180 degrees.");
      if (errors.length) return { errors };
      const diameter = 2 * depth * Math.tan((angle / 2) * PI / 180);
      return wrap({
        values: { "Spot Diameter": dim(diameter, p.unit), "Included Angle": `${angle.toFixed(1)} deg` },
        formulas: ["Spot diameter = 2 x depth x tan(included angle / 2)"],
      });
    },
    tags: ["spot drill", "chamfer", "holemaking"],
  }),
  "annular-cutter-speed": calculator({
    title: "Annular Cutter Speed",
    category: "holemaking",
    description: "Calculate annular cutter RPM from diameter and cutting speed.",
    initial: { unit: "inch", diameter: 1, surfaceSpeed: 90 },
    fields: [unitField, field("diameter", "Cutter Diameter"), field("surfaceSpeed", "Cutting Speed (SFM or m/min)")],
    calculate: (p) => {
      const errors = [];
      const diameter = positive(p.diameter, "Diameter", errors);
      const sfm = positive(p.surfaceSpeed, "Cutting speed", errors);
      if (errors.length) return { errors };
      const rpm = p.unit === "metric" ? (sfm * 1000) / (PI * diameter) : (sfm * 12) / (PI * diameter);
      return wrap({
        values: { "Recommended RPM": round(rpm, 0), "Cutting Speed": speed(sfm, p.unit) },
        formulas: [p.unit === "metric" ? "RPM = (m/min x 1000) / (pi x diameter mm)" : "RPM = (SFM x 12) / (pi x diameter in)"],
        assumptions: ["Use cutter maker data for exact speed/feed, pilot pin lubrication, material, and machine rigidity."],
      });
    },
    tags: ["annular cutter", "mag drill", "rpm"],
  }),
  "go-nogo-limits": calculator({
    title: "Go / No-Go Limit Calculator",
    category: "inspection",
    description: "Calculate upper and lower limits from nominal and bilateral or unilateral tolerances.",
    initial: { unit: "inch", nominal: 1, plusTol: 0.005, minusTol: 0.002 },
    fields: [unitField, field("nominal", "Nominal Dimension"), field("plusTol", "Plus Tolerance"), field("minusTol", "Minus Tolerance")],
    calculate: (p) => {
      const errors = [];
      const nominal = positive(p.nominal, "Nominal", errors);
      const plus = nonNegative(p.plusTol, "Plus tolerance", errors);
      const minus = nonNegative(p.minusTol, "Minus tolerance", errors);
      if (errors.length) return { errors };
      return wrap({
        values: { "Lower Limit": dim(nominal - minus, p.unit), "Upper Limit": dim(nominal + plus, p.unit), "Tolerance Width": dim(plus + minus, p.unit) },
        formulas: ["Lower limit = nominal - minus tolerance", "Upper limit = nominal + plus tolerance"],
      });
    },
    tags: ["limits", "inspection", "tolerance"],
  }),
  "mmc-bonus": calculator({
    title: "MMC / LMC Bonus Tolerance",
    category: "inspection",
    description: "Calculate basic bonus tolerance from feature size departure from MMC.",
    initial: { unit: "inch", featureType: "hole", actualSize: 0.503, mmcSize: 0.5, statedTolerance: 0.006 },
    fields: [unitField, field("featureType", "Feature Type", "select", { options: [["hole", "Hole/Internal"], ["pin", "Pin/External"]] }), field("actualSize", "Actual Feature Size"), field("mmcSize", "MMC Size"), field("statedTolerance", "Stated Position Tolerance")],
    calculate: (p) => {
      const errors = [];
      const actual = positive(p.actualSize, "Actual size", errors);
      const mmc = positive(p.mmcSize, "MMC size", errors);
      const tol = nonNegative(p.statedTolerance, "Stated tolerance", errors);
      if (errors.length) return { errors };
      const bonus = p.featureType === "pin" ? Math.max(0, mmc - actual) : Math.max(0, actual - mmc);
      return wrap({
        values: { "Bonus Tolerance": dim(bonus, p.unit), "Total Positional Tolerance": dim(tol + bonus, p.unit) },
        formulas: ["Hole bonus at MMC = actual size - MMC size", "Pin bonus at MMC = MMC size - actual size", "Total tolerance = stated tolerance + bonus"],
        assumptions: ["Applies only when the feature control frame includes MMC/LMC modifier and datum setup is valid."],
      });
    },
    tags: ["MMC", "bonus", "GD&T", "position"],
  }),
  "sine-bar": calculator({
    title: "Sine Bar Calculator",
    category: "inspection",
    description: "Calculate gauge block height for a sine bar angle.",
    initial: { unit: "inch", sineBarLength: 5, angle: 30 },
    fields: [unitField, field("sineBarLength", "Sine Bar Center Distance"), field("angle", "Angle (deg)")],
    calculate: (p) => {
      const errors = [];
      const length = positive(p.sineBarLength, "Sine bar length", errors);
      const angle = n(p.angle);
      if (angle < 0 || angle >= 90) errors.push("Angle should be from 0 to less than 90 degrees.");
      if (errors.length) return { errors };
      const height = length * Math.sin(angle * PI / 180);
      return wrap({
        values: { "Gauge Block Height": dim(height, p.unit) },
        formulas: ["Gauge block height = sine bar length x sin(angle)"],
      });
    },
    tags: ["sine bar", "gauge blocks", "angle"],
  }),
  "bend-deduction": calculator({
    title: "Bend Deduction",
    category: "sheet-metal",
    description: "Calculate bend deduction from outside setback and bend allowance.",
    initial: { unit: "inch", thickness: 0.125, radius: 0.125, angle: 90, kFactor: 0.33 },
    fields: [unitField, field("thickness", "Thickness"), field("radius", "Inside Radius"), field("angle", "Bend Angle"), field("kFactor", "K-Factor")],
    calculate: (p) => {
      const errors = [];
      const t = positive(p.thickness, "Thickness", errors);
      const r = positive(p.radius, "Inside radius", errors);
      const angle = positive(p.angle, "Bend angle", errors);
      const k = positive(p.kFactor, "K-factor", errors);
      if (errors.length) return { errors };
      const ba = (angle * PI / 180) * (r + k * t);
      const ossb = Math.tan((angle / 2) * PI / 180) * (r + t);
      const bd = 2 * ossb - ba;
      return wrap({
        values: { "Bend Allowance": dim(ba, p.unit), "Outside Setback": dim(ossb, p.unit), "Bend Deduction": dim(bd, p.unit) },
        formulas: ["BA = angle radians x (inside radius + K x thickness)", "OSSB = tan(angle / 2) x (inside radius + thickness)", "BD = 2 x OSSB - BA"],
        assumptions: ["Use shop-confirmed K-factor and tooling data for production flat patterns."],
      });
    },
    tags: ["bend deduction", "flat pattern", "sheet metal"],
  }),
  "flat-pattern": calculator({
    title: "Flat Pattern Length",
    category: "sheet-metal",
    description: "Calculate simple two-leg flat length from leg lengths and bend deduction.",
    initial: { unit: "inch", legA: 2, legB: 2, bendDeduction: 0.18 },
    fields: [unitField, field("legA", "Outside Leg A"), field("legB", "Outside Leg B"), field("bendDeduction", "Bend Deduction")],
    calculate: (p) => {
      const errors = [];
      const a = positive(p.legA, "Leg A", errors);
      const b = positive(p.legB, "Leg B", errors);
      const bd = nonNegative(p.bendDeduction, "Bend deduction", errors);
      if (errors.length) return { errors };
      return wrap({
        values: { "Flat Length": dim(a + b - bd, p.unit) },
        formulas: ["Flat length = outside leg A + outside leg B - bend deduction"],
        assumptions: ["For a single bend. Multi-bend parts require consistent dimensioning convention and bend order review."],
      });
    },
    tags: ["flat pattern", "blank", "bend deduction"],
  }),
  "weld-volume": calculator({
    title: "Weld Volume",
    category: "welding",
    description: "Estimate fillet weld volume from leg size and length.",
    initial: { unit: "inch", length: 12, legSize: 0.25, reinforcementFactor: 1.1 },
    fields: [unitField, field("length", "Weld Length"), field("legSize", "Fillet Leg Size"), field("reinforcementFactor", "Reinforcement Factor")],
    calculate: (p) => {
      const errors = [];
      const length = positive(p.length, "Length", errors);
      const leg = positive(p.legSize, "Leg size", errors);
      const factor = positive(p.reinforcementFactor, "Reinforcement factor", errors);
      if (errors.length) return { errors };
      const volume = 0.5 * leg * leg * length * factor;
      return wrap({
        values: { "Estimated Weld Volume": `${volume.toFixed(4)} ${p.unit === "metric" ? "mm^3" : "in^3"}`, "Cross-section Area": `${(0.5 * leg * leg * factor).toFixed(4)} ${p.unit === "metric" ? "mm^2" : "in^2"}` },
        formulas: ["Fillet area ~= leg^2 / 2", "Weld volume = area x length x reinforcement factor"],
        assumptions: ["Actual volume depends on throat, convexity, joint fit-up, passes, and weld profile."],
      });
    },
    tags: ["filler", "fillet", "volume"],
  }),
  "weld-travel-speed": calculator({
    title: "Weld Travel Speed",
    category: "welding",
    description: "Calculate travel speed from weld length and arc time.",
    initial: { unit: "inch", weldLength: 24, arcMinutes: 3 },
    fields: [unitField, field("weldLength", "Weld Length"), field("arcMinutes", "Arc Time (min)")],
    calculate: (p) => {
      const errors = [];
      const length = positive(p.weldLength, "Weld length", errors);
      const minutes = positive(p.arcMinutes, "Arc time", errors);
      if (errors.length) return { errors };
      return wrap({
        values: { "Travel Speed": `${(length / minutes).toFixed(3)} ${p.unit === "metric" ? "mm/min" : "in/min"}` },
        formulas: ["Travel speed = weld length / arc time"],
      });
    },
    tags: ["travel speed", "welding", "arc time"],
  }),
  "gas-flow-converter": calculator({
    title: "Gas Flow Converter",
    category: "welding",
    description: "Convert shielding gas flow between CFH and L/min.",
    initial: { cfh: 25 },
    fields: [field("cfh", "Gas Flow (CFH)")],
    calculate: (p) => {
      const errors = [];
      const cfh = positive(p.cfh, "CFH", errors);
      if (errors.length) return { errors };
      return wrap({
        values: { "L/min": `${(cfh * 0.471947).toFixed(2)} L/min`, "CFH": `${cfh.toFixed(1)} CFH` },
        formulas: ["1 CFH = 0.471947 L/min"],
      });
    },
    tags: ["argon", "shielding gas", "CFH", "L/min"],
  }),
  "weld-filler-estimate": calculator({
    title: "Filler Metal Estimate",
    category: "welding",
    description: "Estimate filler metal weight from weld volume, metal density, and deposition efficiency.",
    initial: { unit: "inch", weldVolume: 1.5, density: 0.283, depositionEfficiency: 85 },
    fields: [unitField, field("weldVolume", "Weld Metal Volume"), field("density", "Filler Density (lb/in^3 or g/cm^3)"), field("depositionEfficiency", "Deposition Efficiency %")],
    calculate: (p) => {
      const errors = [];
      const volume = positive(p.weldVolume, "Weld volume", errors);
      const density = positive(p.density, "Density", errors);
      const efficiency = positive(p.depositionEfficiency, "Deposition efficiency", errors) / 100;
      if (efficiency > 1) errors.push("Deposition efficiency should be 100% or less.");
      if (errors.length) return { errors };
      const deposited = p.unit === "metric" ? (volume / 1000) * density : volume * density;
      const purchased = deposited / efficiency;
      return wrap({
        values: {
          "Deposited Metal": `${deposited.toFixed(3)} ${p.unit === "metric" ? "g" : "lb"}`,
          "Filler Required": `${purchased.toFixed(3)} ${p.unit === "metric" ? "g" : "lb"}`,
        },
        formulas: ["Deposited metal weight = weld volume x density", "Filler required = deposited metal / deposition efficiency"],
        assumptions: ["Actual filler depends on joint prep, passes, reinforcement, stub loss, spatter, process, and operator technique."],
      });
    },
    tags: ["filler", "deposition", "weld weight", "consumable"],
  }),
  "fillet-weld-throat": calculator({
    title: "Fillet Weld Throat Helper",
    category: "welding",
    description: "Calculate theoretical throat from equal-leg fillet weld size.",
    initial: { unit: "inch", legSize: 0.25 },
    fields: [unitField, field("legSize", "Equal Leg Fillet Size")],
    calculate: (p) => {
      const errors = [];
      const leg = positive(p.legSize, "Fillet leg size", errors);
      if (errors.length) return { errors };
      const throat = leg * 0.70710678;
      return wrap({
        values: { "Theoretical Throat": dim(throat, p.unit), "Leg Size": dim(leg, p.unit) },
        formulas: ["Equal-leg fillet theoretical throat = leg size x sin(45 deg) = 0.7071 x leg size"],
        assumptions: ["Does not verify code-required effective throat, convexity/concavity, penetration, load direction, or weld procedure requirements."],
      });
    },
    tags: ["fillet", "throat", "weld size"],
  }),
  "torque": calculator({
    title: "Torque Calculator",
    category: "mechanical-design",
    description: "Calculate torque from force and lever arm.",
    initial: { force: 100, distance: 12 },
    fields: [field("force", "Force (lbf)"), field("distance", "Lever Arm (in)")],
    calculate: (p) => {
      const errors = [];
      const force = positive(p.force, "Force", errors);
      const distanceValue = positive(p.distance, "Lever arm", errors);
      if (errors.length) return { errors };
      const lbIn = force * distanceValue;
      return wrap({
        values: { "Torque": `${lbIn.toFixed(2)} lb-in`, "Torque lb-ft": `${(lbIn / 12).toFixed(2)} lb-ft`, "Torque N-m": `${(lbIn * 0.112985).toFixed(2)} N-m` },
        formulas: ["Torque = force x perpendicular distance"],
      });
    },
    tags: ["torque", "moment", "force"],
  }),
  "bolt-torque": calculator({
    title: "Bolt Torque Estimate",
    category: "mechanical-design",
    description: "Estimate tightening torque from clamp load, nominal diameter, and nut factor.",
    initial: { clampLoad: 5000, diameter: 0.5, nutFactor: 0.2 },
    fields: [field("clampLoad", "Target Clamp Load (lbf)"), field("diameter", "Nominal Diameter (in)"), field("nutFactor", "Nut Factor K")],
    calculate: (p) => {
      const errors = [];
      const load = positive(p.clampLoad, "Clamp load", errors);
      const diameterValue = positive(p.diameter, "Diameter", errors);
      const k = positive(p.nutFactor, "Nut factor", errors);
      if (errors.length) return { errors };
      const torque = k * load * diameterValue;
      return wrap({
        values: { "Torque": `${torque.toFixed(1)} lb-in`, "Torque lb-ft": `${(torque / 12).toFixed(1)} lb-ft` },
        formulas: ["T = K x D x F"],
        assumptions: ["Nut factor varies strongly with lubrication, coating, thread condition, and washer/bearing surface."],
      });
    },
    tags: ["bolt", "torque", "clamp load"],
  }),
  "bearing-life": calculator({
    title: "Bearing Life Basic L10",
    category: "mechanical-design",
    description: "Calculate basic L10 bearing life from dynamic rating, equivalent load, and speed.",
    initial: { ratingC: 5000, loadP: 1000, rpm: 1000, exponent: 3 },
    fields: [field("ratingC", "Dynamic Load Rating C"), field("loadP", "Equivalent Load P"), field("rpm", "RPM"), field("exponent", "Life Exponent (3 ball, 3.333 roller)")],
    calculate: (p) => {
      const errors = [];
      const c = positive(p.ratingC, "Dynamic rating", errors);
      const load = positive(p.loadP, "Equivalent load", errors);
      const rpm = positive(p.rpm, "RPM", errors);
      const exponent = positive(p.exponent, "Exponent", errors);
      if (errors.length) return { errors };
      const millionRev = (c / load) ** exponent;
      const hours = (millionRev * 1000000) / (60 * rpm);
      return wrap({
        values: { "L10 Life": `${hours.toFixed(1)} hr`, "Life": `${millionRev.toFixed(2)} million rev` },
        formulas: ["L10 = (C / P)^p million revolutions", "Hours = L10 x 1,000,000 / (60 x RPM)"],
        assumptions: ["Basic rating life only; lubrication, contamination, mounting, temperature, shock, and reliability factors matter."],
      });
    },
    tags: ["bearing", "L10", "life"],
  }),
  "spring-rate": calculator({
    title: "Compression Spring Rate",
    category: "mechanical-design",
    description: "Calculate spring rate for a round-wire compression spring.",
    initial: { wireDiameter: 0.08, meanDiameter: 0.75, activeCoils: 8, shearModulus: 11500000 },
    fields: [field("wireDiameter", "Wire Diameter"), field("meanDiameter", "Mean Coil Diameter"), field("activeCoils", "Active Coils"), field("shearModulus", "Shear Modulus")],
    calculate: (p) => {
      const errors = [];
      const d = positive(p.wireDiameter, "Wire diameter", errors);
      const D = positive(p.meanDiameter, "Mean diameter", errors);
      const coils = positive(p.activeCoils, "Active coils", errors);
      const G = positive(p.shearModulus, "Shear modulus", errors);
      if (errors.length) return { errors };
      const rate = (G * d ** 4) / (8 * D ** 3 * coils);
      return wrap({
        values: { "Spring Rate": `${rate.toFixed(3)} force/unit`, "Spring Index": `${(D / d).toFixed(2)}` },
        formulas: ["k = G x d^4 / (8 x D^3 x active coils)"],
        assumptions: ["Basic helical compression spring formula; check stress, solid height, ends, buckling, fatigue, and material limits."],
      });
    },
    tags: ["spring", "rate", "compression"],
  }),
  "gear-pitch-diameter": calculator({
    title: "Gear Pitch Diameter",
    category: "power-transmission",
    description: "Calculate pitch diameter from tooth count and diametral pitch.",
    initial: { teeth: 40, diametralPitch: 10 },
    fields: [field("teeth", "Number of Teeth"), field("diametralPitch", "Diametral Pitch")],
    calculate: (p) => {
      const errors = [];
      const teeth = positive(p.teeth, "Teeth", errors);
      const dp = positive(p.diametralPitch, "Diametral pitch", errors);
      if (errors.length) return { errors };
      return wrap({
        values: { "Pitch Diameter": `${(teeth / dp).toFixed(4)} in`, "Outside Diameter": `${((teeth + 2) / dp).toFixed(4)} in` },
        formulas: ["Pitch diameter = teeth / diametral pitch", "Standard spur gear OD = (teeth + 2) / DP"],
        assumptions: ["Standard full-depth involute approximation. Verify pressure angle, profile shift, and gear standard."],
      });
    },
    tags: ["gear", "pitch diameter", "DP"],
  }),
  "module-gear": calculator({
    title: "Metric Module Gear",
    category: "power-transmission",
    description: "Calculate metric gear pitch diameter and outside diameter from module.",
    initial: { teeth: 40, module: 2 },
    fields: [field("teeth", "Number of Teeth"), field("module", "Module (mm)")],
    calculate: (p) => {
      const errors = [];
      const teeth = positive(p.teeth, "Teeth", errors);
      const module = positive(p.module, "Module", errors);
      if (errors.length) return { errors };
      return wrap({
        values: { "Pitch Diameter": `${(teeth * module).toFixed(3)} mm`, "Outside Diameter": `${((teeth + 2) * module).toFixed(3)} mm` },
        formulas: ["Pitch diameter = teeth x module", "Standard spur gear OD = (teeth + 2) x module"],
      });
    },
    tags: ["module", "metric gear", "pitch diameter"],
  }),
  "pulley-rpm": calculator({
    title: "Pulley RPM",
    category: "power-transmission",
    description: "Calculate driven pulley speed from driver RPM and pulley diameters.",
    initial: { driverRpm: 1750, driverDiameter: 3, drivenDiameter: 6 },
    fields: [field("driverRpm", "Driver RPM"), field("driverDiameter", "Driver Pulley Diameter"), field("drivenDiameter", "Driven Pulley Diameter")],
    calculate: (p) => {
      const errors = [];
      const rpm = positive(p.driverRpm, "Driver RPM", errors);
      const driver = positive(p.driverDiameter, "Driver diameter", errors);
      const driven = positive(p.drivenDiameter, "Driven diameter", errors);
      if (errors.length) return { errors };
      return wrap({
        values: { "Driven RPM": round(rpm * driver / driven, 1), "Speed Ratio": `${(driver / driven).toFixed(3)}:1` },
        formulas: ["Driven RPM = driver RPM x driver diameter / driven diameter"],
        assumptions: ["Does not account for belt slip or pitch diameter differences."],
      });
    },
    tags: ["pulley", "belt", "rpm", "ratio"],
  }),
  "belt-speed": calculator({
    title: "Belt Speed",
    category: "power-transmission",
    description: "Calculate belt surface speed from pulley diameter and RPM.",
    initial: { diameter: 4, rpm: 1750 },
    fields: [field("diameter", "Pulley Diameter (in)"), field("rpm", "RPM")],
    calculate: (p) => {
      const errors = [];
      const diameter = positive(p.diameter, "Diameter", errors);
      const rpm = positive(p.rpm, "RPM", errors);
      if (errors.length) return { errors };
      const fpm = PI * diameter * rpm / 12;
      return wrap({
        values: { "Belt Speed ft/min": `${fpm.toFixed(1)} ft/min`, "Belt Speed m/s": `${(fpm * 0.00508).toFixed(2)} m/s` },
        formulas: ["Belt speed ft/min = pi x diameter in x RPM / 12"],
      });
    },
    tags: ["belt", "surface speed", "fpm"],
  }),
  "chain-sprocket-ratio": calculator({
    title: "Chain / Sprocket Ratio",
    category: "power-transmission",
    description: "Calculate driven sprocket speed and ratio from tooth counts.",
    initial: { driverRpm: 500, driverTeeth: 18, drivenTeeth: 36 },
    fields: [field("driverRpm", "Driver RPM"), field("driverTeeth", "Driver Sprocket Teeth"), field("drivenTeeth", "Driven Sprocket Teeth")],
    calculate: (p) => {
      const errors = [];
      const rpm = positive(p.driverRpm, "Driver RPM", errors);
      const driver = positive(p.driverTeeth, "Driver teeth", errors);
      const driven = positive(p.drivenTeeth, "Driven teeth", errors);
      if (errors.length) return { errors };
      return wrap({
        values: { "Driven RPM": round(rpm * driver / driven, 1), "Reduction Ratio": `${(driven / driver).toFixed(3)}:1` },
        formulas: ["Driven RPM = driver RPM x driver teeth / driven teeth", "Reduction ratio = driven teeth / driver teeth"],
        assumptions: ["Does not include chain wear, polygon effect, or tension/slip issues."],
      });
    },
    tags: ["chain", "sprocket", "ratio", "rpm"],
  }),
  "center-distance": calculator({
    title: "Pulley / Sprocket Center Distance",
    category: "power-transmission",
    description: "Estimate belt or chain center distance from pitch length and pulley pitch diameters.",
    initial: { unit: "inch", pitchLength: 60, largeDiameter: 8, smallDiameter: 4 },
    fields: [unitField, field("pitchLength", "Belt/Chain Pitch Length"), field("largeDiameter", "Large Pitch Diameter"), field("smallDiameter", "Small Pitch Diameter")],
    calculate: (p) => {
      const errors = [];
      const L = positive(p.pitchLength, "Pitch length", errors);
      const D = positive(p.largeDiameter, "Large diameter", errors);
      const d = positive(p.smallDiameter, "Small diameter", errors);
      if (errors.length) return { errors };
      const B = 4 * L - 2 * PI * (D + d);
      const center = (B + Math.sqrt(Math.max(0, B ** 2 - 32 * (D - d) ** 2))) / 16;
      return wrap({
        values: {
          "Estimated Center Distance": dim(center, p.unit),
          "Entered Pitch Length": dim(L, p.unit),
          "Large Pitch Diameter": dim(D, p.unit),
          "Small Pitch Diameter": dim(d, p.unit),
        },
        formulas: [`Approximate open-belt length equation solved for center distance; all length terms use ${p.unit === "metric" ? "millimeters" : "inches"}.`],
        assumptions: ["Use belt/chain manufacturer tables for exact pitch length, tension, wrap angle, and adjustment allowance."],
      });
    },
    tags: ["center distance", "belt", "chain", "pulley"],
  }),
  "diametral-pitch": calculator({
    title: "Diametral Pitch Calculator",
    category: "power-transmission",
    description: "Calculate diametral pitch and circular pitch from gear teeth and pitch diameter.",
    initial: { teeth: 40, pitchDiameter: 4 },
    fields: [field("teeth", "Number of Teeth"), field("pitchDiameter", "Pitch Diameter (in)")],
    calculate: (p) => {
      const errors = [];
      const teeth = positive(p.teeth, "Teeth", errors);
      const pd = positive(p.pitchDiameter, "Pitch diameter", errors);
      if (errors.length) return { errors };
      const dp = teeth / pd;
      return wrap({
        values: { "Diametral Pitch": dp.toFixed(4), "Circular Pitch": `${(PI / dp).toFixed(4)} in` },
        formulas: ["DP = teeth / pitch diameter", "Circular pitch = pi / DP"],
      });
    },
    tags: ["diametral pitch", "gear", "circular pitch"],
  }),
  "circle-calculator": calculator({
    title: "Circle Calculator",
    category: "shop-math",
    description: "Calculate circle area, circumference, and radius/diameter relationships.",
    initial: { unit: "inch", diameter: 1 },
    fields: [unitField, field("diameter", "Diameter")],
    calculate: (p) => {
      const errors = [];
      const diameter = positive(p.diameter, "Diameter", errors);
      if (errors.length) return { errors };
      return wrap({
        values: { "Radius": dim(diameter / 2, p.unit), "Circumference": dim(PI * diameter, p.unit), "Area": `${(PI * diameter ** 2 / 4).toFixed(p.unit === "metric" ? 3 : 4)} ${p.unit === "metric" ? "mm^2" : "in^2"}` },
        formulas: ["Radius = diameter / 2", "Circumference = pi x diameter", "Area = pi x diameter^2 / 4"],
      });
    },
    tags: ["circle", "area", "circumference"],
  }),
  "chord-height": calculator({
    title: "Chord Height / Sagitta",
    category: "shop-math",
    description: "Calculate arc sagitta from radius and chord length.",
    initial: { unit: "inch", radius: 2, chord: 2 },
    fields: [unitField, field("radius", "Radius"), field("chord", "Chord Length")],
    calculate: (p) => {
      const errors = [];
      const radius = positive(p.radius, "Radius", errors);
      const chord = positive(p.chord, "Chord", errors);
      if (chord > 2 * radius) errors.push("Chord cannot exceed diameter.");
      if (errors.length) return { errors };
      const height = radius - Math.sqrt(radius ** 2 - (chord / 2) ** 2);
      return wrap({
        values: { "Chord Height": dim(height, p.unit), "Included Angle": `${(2 * Math.asin(chord / (2 * radius)) * 180 / PI).toFixed(3)} deg` },
        formulas: ["Sagitta = R - sqrt(R^2 - (chord / 2)^2)"],
      });
    },
    tags: ["chord", "sagitta", "radius"],
  }),
  "radius-from-chord": calculator({
    title: "Radius from Chord",
    category: "shop-math",
    description: "Calculate radius from chord length and chord height.",
    initial: { unit: "inch", chord: 2, height: 0.25 },
    fields: [unitField, field("chord", "Chord Length"), field("height", "Chord Height")],
    calculate: (p) => {
      const errors = [];
      const chord = positive(p.chord, "Chord", errors);
      const height = positive(p.height, "Height", errors);
      if (errors.length) return { errors };
      const radius = (height / 2) + (chord ** 2) / (8 * height);
      return wrap({
        values: { "Radius": dim(radius, p.unit), "Diameter": dim(radius * 2, p.unit) },
        formulas: ["Radius = h / 2 + chord^2 / (8h)"],
      });
    },
    tags: ["radius", "chord", "layout"],
  }),
  "density-reference": calculator({
    title: "Density Reference",
    category: "materials",
    description: "Look up common material densities used in shop estimating.",
    initial: { material: "mild_steel" },
    fields: [field("material", "Material", "select", { options: materialOptions })],
    calculate: (p) => {
      const material = materialDensity[p.material] || materialDensity.mild_steel;
      return wrap({
        values: { "Material": material.label, "Density": `${material.lbIn3} lb/in^3`, "Metric Density": `${material.gCm3} g/cm^3` },
        formulas: ["Weight = volume x density"],
        assumptions: ["Alloy-specific density can vary. Use supplier data when weight or quote accuracy matters."],
      });
    },
    tags: ["density", "material", "weight"],
  }),
  "hardness-conversion": calculator({
    title: "Hardness Conversion Reference",
    category: "materials",
    description: "Approximate Brinell to Rockwell conversion for shop reference.",
    initial: { brinell: 200 },
    fields: [field("brinell", "Brinell Hardness HB")],
    calculate: (p) => {
      const errors = [];
      const hb = positive(p.brinell, "Brinell hardness", errors);
      if (errors.length) return { errors };
      const hrc = hb < 180 ? null : Math.min(65, Math.max(10, 0.105 * hb - 8));
      const hrb = hb >= 180 ? null : Math.min(100, Math.max(35, 0.55 * hb + 2));
      return wrap({
        values: { "Approx. Rockwell": hrc ? `${hrc.toFixed(0)} HRC` : `${hrb.toFixed(0)} HRB` },
        formulas: ["Approximate conversion curve fit from common hardness conversion tables"],
        assumptions: ["Hardness conversions are approximate and material dependent. Use the specified scale on certified inspection records."],
      });
    },
    tags: ["hardness", "Rockwell", "Brinell", "HRC", "HRB"],
  }),
  "material-cost": calculator({
    title: "Material Cost Estimate",
    category: "materials",
    description: "Estimate material cost from weight, cost per pound/kg, scrap, and quantity.",
    initial: { weightPerPart: 2.5, quantity: 10, costPerWeight: 3.25, scrapPercent: 8 },
    fields: [field("weightPerPart", "Weight per Part"), field("quantity", "Quantity"), field("costPerWeight", "Cost per lb/kg"), field("scrapPercent", "Scrap Allowance %")],
    calculate: (p) => {
      const errors = [];
      const weight = positive(p.weightPerPart, "Weight per part", errors);
      const qty = positive(p.quantity, "Quantity", errors);
      const cost = nonNegative(p.costPerWeight, "Cost per weight", errors);
      const scrap = nonNegative(p.scrapPercent, "Scrap allowance", errors) / 100;
      if (errors.length) return { errors };
      const totalWeight = weight * qty * (1 + scrap);
      return wrap({
        values: { "Buy Weight": totalWeight.toFixed(3), "Estimated Material Cost": money(totalWeight * cost) },
        formulas: ["Buy weight = part weight x quantity x (1 + scrap allowance)", "Material cost = buy weight x cost per weight"],
      });
    },
    tags: ["material cost", "weight", "quote"],
  }),
  "plate-weight": calculator({
    title: "Plate Weight",
    category: "materials",
    description: "Estimate plate or rectangular blank weight.",
    initial: { unit: "inch", material: "mild_steel", length: 12, width: 6, thickness: 0.25, quantity: 1 },
    fields: [unitField, field("material", "Material", "select", { options: materialOptions }), field("length", "Length"), field("width", "Width"), field("thickness", "Thickness"), field("quantity", "Quantity")],
    calculate: calculateRectWeight,
    tags: ["plate", "weight", "density"],
  }),
  "bar-stock-weight": calculator({
    title: "Bar Stock Weight",
    category: "materials",
    description: "Estimate round bar weight from diameter, length, and material density.",
    initial: { unit: "inch", material: "mild_steel", diameter: 1, length: 12, quantity: 1 },
    fields: [unitField, field("material", "Material", "select", { options: materialOptions }), field("diameter", "Diameter"), field("length", "Length"), field("quantity", "Quantity")],
    calculate: calculateCylWeight,
    tags: ["bar", "round", "weight"],
  }),
  "markup-margin": calculator({
    title: "Markup / Margin",
    category: "costing",
    description: "Convert cost to price using either markup or margin.",
    initial: { cost: 100, markup: 30, margin: 25 },
    fields: [field("cost", "Cost"), field("markup", "Markup %"), field("margin", "Margin %")],
    calculate: (p) => {
      const errors = [];
      const cost = positive(p.cost, "Cost", errors);
      const markup = nonNegative(p.markup, "Markup", errors) / 100;
      const margin = nonNegative(p.margin, "Margin", errors) / 100;
      if (margin >= 1) errors.push("Margin must be less than 100%.");
      if (errors.length) return { errors };
      return wrap({
        values: { "Price from Markup": money(cost * (1 + markup)), "Price from Margin": money(cost / (1 - margin)), "Gross Profit at Margin": money(cost / (1 - margin) - cost) },
        formulas: ["Price with markup = cost x (1 + markup)", "Price with margin = cost / (1 - margin)"],
      });
    },
    tags: ["markup", "margin", "quote", "price"],
  }),
  "part-cost-estimate": calculator({
    title: "Part Cost Estimate",
    category: "costing",
    description: "Estimate total and per-part cost from material, setup, cycle, machine rate, and consumables.",
    initial: { materialCost: 120, setupHours: 1, cycleMinutes: 8, quantity: 20, machineRate: 95, consumables: 25 },
    fields: [field("materialCost", "Material Cost"), field("setupHours", "Setup Hours"), field("cycleMinutes", "Cycle Minutes per Part"), field("quantity", "Quantity"), field("machineRate", "Machine Rate / hr"), field("consumables", "Consumables / Tooling")],
    calculate: (p) => {
      const errors = [];
      const material = nonNegative(p.materialCost, "Material cost", errors);
      const setup = nonNegative(p.setupHours, "Setup hours", errors);
      const cycle = positive(p.cycleMinutes, "Cycle minutes", errors);
      const qty = positive(p.quantity, "Quantity", errors);
      const rate = nonNegative(p.machineRate, "Machine rate", errors);
      const consumables = nonNegative(p.consumables, "Consumables", errors);
      if (errors.length) return { errors };
      const hours = setup + (cycle * qty) / 60;
      const total = material + hours * rate + consumables;
      return wrap({
        values: { "Machine Hours": `${hours.toFixed(2)} hr`, "Total Cost": money(total), "Cost per Part": money(total / qty) },
        formulas: ["Machine hours = setup hours + cycle minutes x quantity / 60", "Total cost = material + machine hours x rate + consumables"],
      });
    },
    tags: ["part cost", "estimate", "quote"],
  }),
  "tooling-cost": calculator({
    title: "Tooling Cost Calculator",
    category: "costing",
    description: "Estimate tooling cost per part from tool price, usable edges, parts per edge, and batch quantity.",
    initial: { toolCost: 60, usableEdges: 4, partsPerEdge: 25, quantity: 100 },
    fields: [field("toolCost", "Tool Cost"), field("usableEdges", "Usable Edges"), field("partsPerEdge", "Parts per Edge"), field("quantity", "Batch Quantity")],
    calculate: (p) => {
      const errors = [];
      const cost = positive(p.toolCost, "Tool cost", errors);
      const edges = positive(p.usableEdges, "Usable edges", errors);
      const parts = positive(p.partsPerEdge, "Parts per edge", errors);
      const qty = positive(p.quantity, "Quantity", errors);
      if (errors.length) return { errors };
      const costPerPart = cost / (edges * parts);
      return wrap({
        values: { "Tool Cost per Part": money(costPerPart), "Batch Tooling Cost": money(costPerPart * qty) },
        formulas: ["Tool cost per part = tool cost / (usable edges x parts per edge)"],
        assumptions: ["Does not include breakage, regrind cost, setup scrap, or shop tool crib burden."],
      });
    },
    tags: ["tooling", "cost", "insert", "quote"],
  }),
  "machine-time": calculator({
    title: "Machine Time",
    category: "costing",
    description: "Estimate total machine hours from setup, cycle time, and quantity.",
    initial: { setupHours: 1, cycleMinutes: 6, quantity: 25, efficiency: 85 },
    fields: [field("setupHours", "Setup Hours"), field("cycleMinutes", "Cycle Minutes per Part"), field("quantity", "Quantity"), field("efficiency", "Efficiency %")],
    calculate: (p) => {
      const errors = [];
      const setup = nonNegative(p.setupHours, "Setup hours", errors);
      const cycle = positive(p.cycleMinutes, "Cycle time", errors);
      const qty = positive(p.quantity, "Quantity", errors);
      const efficiency = positive(p.efficiency, "Efficiency", errors) / 100;
      if (errors.length) return { errors };
      const run = (cycle * qty) / 60 / efficiency;
      return wrap({
        values: { "Run Hours": `${run.toFixed(2)} hr`, "Total Machine Hours": `${(setup + run).toFixed(2)} hr` },
        formulas: ["Run hours = cycle minutes x quantity / 60 / efficiency", "Total hours = setup hours + run hours"],
      });
    },
    tags: ["machine time", "cycle", "quote"],
  }),
  "batch-pricing": calculator({
    title: "Batch Pricing",
    category: "costing",
    description: "Estimate per-part price from fixed cost, variable cost, quantity, and margin.",
    initial: { fixedCost: 250, variableCost: 12, quantity: 50, margin: 30 },
    fields: [field("fixedCost", "Fixed Cost"), field("variableCost", "Variable Cost per Part"), field("quantity", "Quantity"), field("margin", "Profit Margin %")],
    calculate: (p) => {
      const errors = [];
      const fixed = nonNegative(p.fixedCost, "Fixed cost", errors);
      const variable = nonNegative(p.variableCost, "Variable cost", errors);
      const qty = positive(p.quantity, "Quantity", errors);
      const margin = nonNegative(p.margin, "Margin", errors) / 100;
      if (margin >= 1) errors.push("Margin must be less than 100%.");
      if (errors.length) return { errors };
      const cost = fixed + variable * qty;
      const quote = cost / (1 - margin);
      return wrap({
        values: { "Total Cost": money(cost), "Suggested Quote": money(quote), "Price per Part": money(quote / qty) },
        formulas: ["Total cost = fixed cost + variable cost x quantity", "Quote = total cost / (1 - margin)"],
      });
    },
    tags: ["batch", "pricing", "quote"],
  }),
};

export const PROFESSIONAL_CALCULATOR_LABELS = Object.fromEntries(
  Object.entries(PROFESSIONAL_CALCULATORS).map(([id, config]) => [id, config.title])
);
